/*
 * The two screens that belong to the router itself rather than to an area: where "/" lands, and
 * what an unknown path shows.
 *
 * "/" is three different things. On the tab's first address it is a redirect into the conversation
 * the reader was last in. Asked for afterwards — the rail's *Home* — it is the **catch-up screen**
 * (VISUAL.md §7.8): a greeting by the hour, one line saying what is waiting, and the modules' cards
 * of what to open first, because for a seven-person team "what is waiting for me" is the one question
 * no other surface answers. And for an account that has never been greeted it is the **first run**
 * (§7.16), one screen per standing: the administrator of an instance that is still being set up gets
 * the checklist, a guest the three doors of a first minute under a lead that says what a guest sees,
 * and everybody else the same doors under the team's lead. Below 768 px "/" is the sidebar,
 * because there the panes are routes and a grid of cards beside a list of the same conversations says
 * everything twice; the greeting and its one line stand *above* that list, and so does the first run,
 * rather than instead of it.
 *
 * Home decides two things the screens themselves do not: a person who has not been greeted yet is
 * *not* redirected anywhere — a greeting somebody is redirected away from before reading it is no
 * greeting — and which conversation the doors offer is this file's answer, the same one the landing
 * redirect uses, because "where does this reader most want to be" is asked once.
 *
 * Nothing here is loaded lazily, deliberately: a `lazy()` boundary inside the entry makes the
 * bundler split what the entry and the lazy chunk share into chunks of their own, and the shell grew
 * by seven kilobytes when the first-run screens were tried that way — more than the screens weigh.
 */

import { Link, useNavigate } from "@tanstack/react-router"
import { Fragment, useEffect, useMemo } from "react"
import { LOCALE, t, timeZone, translate } from "../core/i18n/index.ts"
import { setupDoorOf } from "../core/modules/registry.ts"
import { useSlot } from "../core/modules/slots.ts"
import { isAdmin, isGuest } from "../core/people/standing.ts"
import { firstName } from "../core/shell/firstName.ts"
import { FirstRunAdmin } from "../core/shell/FirstRunAdmin.tsx"
import { ROCKET_IN_SPACE } from "../core/shell/FirstRunCard.tsx"
import { FirstRunGuest } from "../core/shell/FirstRunGuest.tsx"
import { FirstRunMember } from "../core/shell/FirstRunMember.tsx"
import "../core/shell/home.css"
import { NotFoundState, useFirstChannel, useLayoutMode, useModel } from "../core/shell/index.ts"
import { useShellSetting } from "../core/shell/preferences.tsx"
import { SetupChecklist, setupPending, type PlaceDoor } from "../core/shell/SetupChecklist.tsx"
import { Sidebar } from "../core/shell/Sidebar.tsx"
import type { ConversationSlice, Model } from "../core/store/model.ts"
import { catchUpTotals } from "../core/store/selectors.ts"
import { Button, ListState } from "../ui/index.ts"
import { conversationHref, endColdStart, isColdStart, lastRoute } from "./links.ts"

/**
 * Where "/" sends a reader (UX §1.3): the conversation they were last in, else the first one with
 * something unread, else the first channel — and, failing all three, whatever conversation there
 * is, because a person whose whole day is direct messages should not be shown an empty page.
 *
 * A conversation this build cannot address — a direct conversation whose generated handle the
 * bootstrap did not carry — is skipped rather than turned into a broken path.
 *
 * `conversationOrder` is the reader's own conversations and nothing else: a channel they left or are
 * merely reading by its address is in the model but not in that list (chat's reducer), so "/" never
 * lands somewhere the reader cannot write.
 */
function landingPath(model: Model): string | null {
	const remembered = lastRoute()
	if (remembered !== null) return remembered
	const live = model.conversationOrder
		.map((id) => model.conversations[id])
		.filter(
			(slice): slice is ConversationSlice =>
				slice !== undefined && slice.meta.archivedAt === undefined,
		)
	const isChannel = (slice: ConversationSlice) =>
		slice.meta.kind === "channel" || slice.meta.kind === "private"
	const ranked = [...live.filter((slice) => slice.unread > 0), ...live.filter(isChannel), ...live]
	for (const slice of ranked) {
		const href = conversationHref(slice.meta)
		if (href !== null) return href
	}
	return null
}

export function HomePage() {
	const mode = useLayoutMode()
	const navigate = useNavigate()
	// The model is hydrated after the first render, so the landing path is watched rather than read
	// once: the redirect happens as soon as there is a conversation to redirect into.
	const landing = useModel(landingPath)
	const [seenWelcome] = useShellSetting("core.shell.seen_welcome")
	const channel = useFirstChannel()
	/*
	 * Whether this screen is greeting somebody (X-A4 N15). "Not greeted yet" is only an answer about a
	 * person the model knows: before the bootstrap arrives `me` is null and the settings map is empty,
	 * and treating that as "greet them" would show the screen to every returning reader for as long as
	 * their own answer takes to arrive — and hold back the landing redirect while it did.
	 */
	const greeting = useModel((model) => model.me !== null) && !seenWelcome
	// The standing chooses the screen (VISUAL.md §7.16): the checklist is the administrator's first
	// day on an instance that is still being set up; an administrator of a finished one is greeted
	// like everybody else, because the steps they would be shown are all ticked.
	const firstDay = useModel((model) => isAdmin(model.me) && setupPending(model))
	const guest = useModel((model) => isGuest(model.me))

	// Only the tab's first address is a landing. A later "/" is the rail's *Home*, which is a
	// person asking for this screen and not for the conversation they just left (G-52) — and a person
	// being greeted is not redirected out of the greeting: the greeting *is* what this visit landed
	// on, so dismissing it leaves them here rather than handing them to the redirect.
	useEffect(() => {
		if (greeting) {
			endColdStart()
			return
		}
		if (mode === "narrow" || landing === null || !isColdStart()) return
		void navigate({ to: landing, replace: true })
	}, [mode, landing, greeting, navigate])

	if (greeting) {
		const Screen = firstDay ? FirstRunAdmin : guest ? FirstRunGuest : FirstRunMember
		const screen = <Screen channel={channel} compact={mode === "narrow"} />
		// Below 768 px "/" is the sidebar, and a person's first screen is not a list of channels
		// they have no reason to trust yet — so the greeting stands above the list, and the list
		// scrolls with it (D-10 D10).
		if (mode === "narrow") {
			return (
				<div className="tb-home--narrow">
					{screen}
					<Sidebar />
				</div>
			)
		}
		return screen
	}
	// Below 768 px "/" is the channel list — the panes are routes there, and a grid of cards beside a
	// list of the same conversations is the same information twice. What does stand above the list is
	// the greeting and the line that says what is waiting: a phone screen whose only headings belonged
	// to the navigation region had no `h1` at all, and the one sentence that says "five of them name
	// you" is worth more on a small screen than on a large one (N4-R1-3).
	if (mode === "narrow") {
		return (
			<div className="tb-home--narrow">
				<CatchUpHead compact />
				<Sidebar />
			</div>
		)
	}
	return <CatchUp channel={channel} />
}

/**
 * The greeting and the one line under it: who this is and what is waiting. It is a component of its
 * own because both shapes of Home draw it — the wide catch-up screen above its cards, and the phone's
 * channel list above the list.
 */
function CatchUpHead({ compact = false }: { compact?: boolean }) {
	const name = useModel((model) => firstName(model.me?.displayName ?? ""))
	// The line and the cards are one statement, so they are counted over one set: `catchUpTotals`
	// walks the conversations the cards are drawn from and nothing else. The rail's totals cross every
	// slice the model holds — an archived conversation included — and reading those here told a reader
	// about a mention that no card on the screen led to (N4-R2-2).
	const conversations = useModel((model) => model.conversations)
	const order = useModel((model) => model.conversationOrder)
	const totals = useMemo(
		() => catchUpTotals({ conversations, conversationOrder: order }),
		[conversations, order],
	)
	// "14 unread in 6 conversations · 2 mentions" — the mentions only when there are any.
	const lead = [
		`${t("shell.home.unread", { count: totals.messages })} ${t("shell.home.inConversations", { count: totals.conversations })}`,
		...(totals.mentions > 0 ? [t("shell.home.mentions", { count: totals.mentions })] : []),
	].join(" · ")
	return (
		<div className={compact ? "tb-page__head tb-home__head--compact" : "tb-page__head"}>
			<h1>{t(greetingKey(), { name })}</h1>
			{totals.conversations > 0 ? <p className="tb-page__lead">{lead}</p> : null}
		</div>
	)
}

/**
 * The hour's greeting, in the account's own zone (`core/i18n/format.ts` holds it once the bootstrap
 * has said which one): morning until noon, afternoon until six, evening otherwise. The hour is read
 * with `hourCycle: "h23"` so that midnight is 0 and never the "24" some engines answer for `hour12: false`.
 */
export function greetingKey(
	now: number = Date.now(),
): "shell.home.greeting.morning" | "shell.home.greeting.afternoon" | "shell.home.greeting.evening" {
	const hour = Number(
		new Intl.DateTimeFormat(LOCALE, {
			hour: "numeric",
			hourCycle: "h23",
			timeZone: timeZone(),
		}).format(now),
	)
	if (hour >= 5 && hour < 12) return "shell.home.greeting.morning"
	if (hour >= 12 && hour < 18) return "shell.home.greeting.afternoon"
	return "shell.home.greeting.evening"
}

/**
 * Home once the greeting is behind the reader (VISUAL.md §7.8): who they are and what time it is,
 * what is waiting in one line, and then the modules' sections — chat's cards of the unread
 * conversations and its threads with new replies — in the order they registered. Core draws the
 * two states the sections cannot: a reader in no conversation at all is offered the modules' doors
 * (`homeActions`) — a guest with a sentence of their own, because nothing of the team's is theirs until
 * somebody adds them — and a reader who has read everything is told so, under the rocket, with the
 * channel where everybody is as the one door. The setup checklist stands below for as long as the
 * instance is new — on the administrator's Home, because setting the place up is their work.
 */
function CatchUp({ channel }: { channel: PlaceDoor | undefined }) {
	const joined = useModel((model) => model.conversationOrder.length > 0)
	// Setting the instance up is the administrator's work and the checklist is their card (§7.16): the
	// row that opens it asks for a password nobody else can give, and an invited member was shown
	// "Create the channel everybody joins" as the first thing the product asked of them (N5-R1-2). The
	// card decides for itself whether the instance is still new; who it is addressed to is decided here,
	// by the same predicate that picks the administrator's first screen above.
	const readerIsAdmin = useModel((model) => isAdmin(model.me))
	const guest = useModel((model) => isGuest(model.me))
	const sections = useSlot("homeSections")
	// A primitive, not the object: `catchUpTotals` answers a fresh one, and the head above draws the
	// numbers themselves. Something is waiting while a conversation has unread lines **or** a module's
	// section says it holds something — an AI chat with a question for the reader is not a
	// conversation, and "You're up to date" above it would be a sentence the page itself contradicts.
	const waiting = useModel(
		(model) =>
			catchUpTotals(model).conversations > 0 ||
			sections.some((section) => section.hasItems?.(model) === true),
	)
	const actions = useSlot("homeActions")
	const SetupDoor = setupDoorOf(sections)

	// The one door out of an empty Home for a reader who has joined nothing: the module's primary
	// action (*Browse channels*), else its first.
	const door = actions.find((action) => action.primary === true) ?? actions[0]

	return (
		<div className="tb-page tb-home">
			<CatchUpHead />
			{!joined ? (
				<div className="tb-home__empty">
					<ListState.Empty
						title={t(guest ? "shell.home.guestEmpty.title" : "shell.home.nothingJoined.title")}
						lead={t(guest ? "shell.home.guestEmpty.lead" : "shell.home.nothingJoined.lead")}
						action={
							door === undefined ? undefined : (
								<Button
									variant="primary"
									label={translate(door.labelKey)}
									onClick={() => door.onSelect()}
								/>
							)
						}
					/>
				</div>
			) : !waiting ? (
				<div className="tb-home__empty">
					<ListState.Empty
						illustration={<img src={ROCKET_IN_SPACE} alt="" />}
						title={t("shell.home.upToDate.title")}
						lead={t("shell.home.upToDate.lead")}
						action={
							channel === undefined ? undefined : (
								<Link to={channel.href} className="tb-button tb-button--primary">
									{t("shell.home.open", { channel: channel.label })}
								</Link>
							)
						}
					/>
				</div>
			) : null}
			{[...sections]
				.sort((a, b) => a.order - b.order)
				.map((section) => (
					<Fragment key={section.id}>{section.render()}</Fragment>
				))}
			{readerIsAdmin ? <SetupChecklist channel={channel} SetupDoor={SetupDoor} /> : null}
		</div>
	)
}

/**
 * An address nobody claims (VISUAL.md §7.17). For a signed-in reader this hangs off `shellRoute`,
 * so the rail and the sidebar are still there and the reader is one click from anywhere; on the
 * sign-in screens the router falls back to the same component without a shell around it, which is
 * the one case where a bare page is right (X-A3 V11, X-A1 D22). Two doors: Home, and the channel
 * where everybody is when the model knows one.
 */
export function NotFoundPage() {
	return <NotFoundState />
}
